import { FiBox, FiPackage, FiShoppingBag, FiTruck, FiZap, FiLayers } from "react-icons/fi";
import { IOS } from "../activity-logs/labels";

/**
 * Como a API entrega os dados (o desenho do dashboard depende disso):
 *
 * - GET /creditos/lista devolve UMA LINHA POR TIPO DE CRÉDITO, já somada, e os
 *   KPIs. documentoOrigem (data e fornecedor) é só um documento de amostra do
 *   grupo: não serve para agrupar por mês nem por fornecedor.
 * - Filtrar a lista por um único mês dá o total real daquele mês. A série mensal
 *   sai dessas chamadas (ver useMonthlySeries).
 * - GET /creditos/agrupado/:tipo traz os fornecedores de um tipo, mas ignora os
 *   filtros de data: os valores por fornecedor são sempre do projeto inteiro.
 */

export const LIST_LIMIT = 1000;

export const EMPTY_KPIS = { totalCreditos: 0, icms: 0, ipi: 0, pisCofins: 0, totalOportunidades: 0 };

// Cores vivas, sempre em áreas pequenas: ícones, pontos e linhas finas
export const TAXES = [
  { key: "ICMS", label: "ICMS", dataKey: "icms", kpi: "icms", color: IOS.blue },
  { key: "IPI", label: "IPI", dataKey: "ipi", kpi: "ipi", color: IOS.purple },
  { key: "PIS/COFINS", label: "PIS/COFINS", dataKey: "pis", kpi: "pisCofins", color: IOS.green },
];

export const taxMeta = (key) => TAXES.find((t) => t.key === key) || TAXES[2];

export const CATEGORIES = {
  ativo_imobilizado: { label: "Ativo Imobilizado", icon: FiBox, color: IOS.indigo },
  insumos_sem_credito: { label: "Insumos sem Crédito", icon: FiPackage, color: IOS.blue },
  uso_consumo: { label: "Uso e Consumo", icon: FiShoppingBag, color: IOS.orange },
  frete: { label: "Frete", icon: FiTruck, color: IOS.teal },
  energia: { label: "Energia Elétrica", icon: FiZap, color: IOS.yellow },
  outros: { label: "Outros", icon: FiLayers, color: IOS.gray },
};

export const categoryMeta = (key) => CATEGORIES[key] || CATEGORIES.outros;

const mapCategoria = (tipoCredito) => {
  if (!tipoCredito) return "outros";
  if (tipoCredito.includes("CIAP") || tipoCredito.includes("IMOBILIZADO")) return "ativo_imobilizado";
  if (tipoCredito.includes("INSUMOS")) return "insumos_sem_credito";
  if (tipoCredito.includes("USO_CONSUMO")) return "uso_consumo";
  if (tipoCredito.includes("FRETE")) return "frete";
  if (tipoCredito.includes("ENERGIA")) return "energia";
  return "outros";
};

const taxOf = (tipoCredito) => {
  if (tipoCredito?.includes("ICMS")) return "ICMS";
  if (tipoCredito?.includes("IPI")) return "IPI";
  return "PIS/COFINS";
};

// Converte uma linha de /creditos/lista (um tipo de crédito) para o formato da tela.
export const mapCredito = (c) => {
  const categoria = mapCategoria(c.tipoCredito);
  return {
    id: c.id || c.tipoCredito,
    tipo: taxOf(c.tipoCredito),
    categoria,
    // A primeira observação da API descreve a oportunidade; a categoria sozinha
    // não diferencia tipos como frete de ICMS e frete de PIS/COFINS.
    titulo: c.observacoes?.[0] || categoryMeta(categoria).label,
    valor: Number(c.calculo?.valorCredito) || 0,
    quantidade: Number(c.quantidade) || 0,
    tipoCredito: c.tipoCredito,
  };
};

// ─── Formatação ─────────────────────────────────────────────────────────────
const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const brlCompact = new Intl.NumberFormat("pt-BR", {
  style: "currency", currency: "BRL", notation: "compact", maximumFractionDigits: 1,
});

export const formatCurrency = (v) => brl.format(v || 0);
export const formatCompact = (v) => brlCompact.format(v || 0);

// "R$ 1.234,56" separado em moeda, parte inteira e centavos
export const currencyParts = (v) => {
  const parts = brl.formatToParts(v || 0);
  const join = (...types) => parts.filter((p) => types.includes(p.type)).map((p) => p.value).join("");
  return {
    currency: join("currency"),
    integer: join("minusSign", "integer", "group"),
    decimal: join("decimal", "fraction"),
  };
};
export const formatPercent = (v) => `${(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
export const formatCount = (v) => (v || 0).toLocaleString("pt-BR");

export const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

export const formatMonth = (key) => {
  if (!key) return "";
  const [y, m] = key.split("-");
  if (!y || !m) return key;
  return `${MONTHS[parseInt(m, 10) - 1]}/${y.slice(-2)}`;
};

export const formatCNPJ = (cnpj) => {
  const digits = String(cnpj || "").replace(/\D/g, "");
  if (digits.length !== 14) return cnpj || "";
  return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

const LOWER_WORDS = new Set(["de", "da", "do", "das", "dos", "e"]);
const UPPER_WORDS = new Set(["SA", "S/A", "S.A.", "ME", "EPP", "EIRELI", "BR", "EPI"]);

// "GENERAL MOTORS DO BRASIL LTDA" vira "General Motors do Brasil Ltda". Só altera a
// exibição, e só em nomes todos em maiúsculas; nome com grafia própria fica como está.
export const displayName = (name) => {
  const text = String(name || "");
  if (!text || text !== text.toLocaleUpperCase("pt-BR")) return text;
  return text.trim().split(/\s+/).map((word, i) => {
    const lower = word.toLocaleLowerCase("pt-BR");
    if (i > 0 && LOWER_WORDS.has(lower)) return lower;
    if (UPPER_WORDS.has(word) || /^[A-Z]\.?$/.test(word) || /^\d/.test(word)) return word;
    // Maiúscula também depois de ponto, hífen ou barra: "IND.COM.LTDA" vira "Ind.Com.Ltda"
    return lower.replace(/(^|[.\-/])(\p{L})/gu, (_, sep, letter) => sep + letter.toLocaleUpperCase("pt-BR"));
  }).join(" ");
};

// ─── Meses ──────────────────────────────────────────────────────────────────
const monthIndex = (key) => parseInt(key.slice(0, 4), 10) * 12 + parseInt(key.slice(5), 10) - 1;
const monthFromIndex = (i) => `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;

export const currentMonth = () => {
  const d = new Date();
  return monthFromIndex(d.getFullYear() * 12 + d.getMonth());
};

// Meses de `end` até `start`, do mais recente para o mais antigo
export const monthsDescending = (end, start) => {
  const out = [];
  for (let i = monthIndex(end); i >= monthIndex(start); i -= 1) out.push(monthFromIndex(i));
  return out;
};

export const monthsBefore = (end, count) => monthFromIndex(monthIndex(end) - count + 1);

// Totais de um mês a partir das linhas de /creditos/lista filtradas por ele
export const summarizeMonth = (rows) => {
  const month = { icms: 0, ipi: 0, pis: 0, total: 0, tipos: {} };
  rows.map(mapCredito).forEach((c) => {
    month[taxMeta(c.tipo).dataKey] += c.valor;
    month.total += c.valor;
    month.tipos[c.tipoCredito] = (month.tipos[c.tipoCredito] || 0) + c.valor;
  });
  return month;
};

// Série contínua entre o primeiro e o último mês com valor: meses sem crédito
// entram zerados para o eixo não esconder os buracos.
export const buildSeries = (byMonth) => {
  const keys = [...byMonth.keys()].filter((k) => byMonth.get(k).total > 0).sort();
  if (keys.length === 0) return [];
  return monthsDescending(keys[keys.length - 1], keys[0]).reverse().map((key) => ({
    key,
    label: formatMonth(key),
    ...(byMonth.get(key) || { icms: 0, ipi: 0, pis: 0, total: 0 }),
  }));
};

// ─── Agregações ─────────────────────────────────────────────────────────────
export const buildDashboard = (creditos) => {
  const byCategory = {};
  creditos.forEach((c) => {
    const row = byCategory[c.categoria] || (byCategory[c.categoria] = { key: c.categoria, total: 0, count: 0 });
    row.total += c.valor;
    row.count += c.quantidade;
  });
  return {
    categories: Object.values(byCategory).sort((a, b) => b.total - a.total),
    topCreditos: [...creditos].sort((a, b) => b.valor - a.valor).slice(0, 5),
    itemCount: creditos.reduce((sum, c) => sum + c.quantidade, 0),
  };
};

// Junta os fornecedores de cada tipo (/creditos/agrupado/:tipo) em um só ranking.
export const buildSuppliers = (groups) => {
  const byId = {};
  groups.forEach(({ credito, fornecedores }) => {
    fornecedores.forEach((f) => {
      const id = f.cnpj || f.codigo || f.nome;
      if (!id) return;
      const s = byId[id] || (byId[id] = {
        id,
        name: f.nome || formatCNPJ(f.cnpj) || f.codigo,
        cnpj: f.cnpj || "",
        uf: f.uf || "",
        total: 0,
        count: 0,
        byTax: {},
        tipos: [],
      });
      const valor = Number(f.totalCredito) || 0;
      s.total += valor;
      s.count += Number(f.quantidade) || 0;
      s.byTax[credito.tipo] = (s.byTax[credito.tipo] || 0) + valor;
      s.tipos.push({ credito, fornecedor: f, valor, quantidade: Number(f.quantidade) || 0 });
    });
  });
  return Object.values(byId)
    .map((s) => ({ ...s, tipos: s.tipos.sort((a, b) => b.valor - a.valor) }))
    .sort((a, b) => b.total - a.total);
};
